import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../Components/PageHeader/PageHeader';
import { ApiHit } from '../../Api/ApiHit';
import { searchRole, deleteRole, searchUser } from '../../Constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../Redux/Actions/ApiAction';
import AppTable from '../../Components/AppTable/AppTable';
import { setPagination } from '../../Redux/Actions/TableAction';
import toast from 'react-hot-toast';
import ActionButton from '../../Components/AppButton/ActionButton';
import DeleteActionButton from '../../Components/AppButton/DeleteActionButton';
import { PencilIcon } from 'lucide-react';
import { hasPermission } from '../../utils';

function RoleMaster() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Get user permissions
    const canEditRoles = hasPermission('rolesAndPermissions', 'write');
    const canReadRoles = hasPermission('rolesAndPermissions', 'read');
    const canDeleteRoles = hasPermission('rolesAndPermissions', 'delete');
    const canAddRoles = hasPermission('rolesAndPermissions', 'write');

    useEffect(() => {
        if (canReadRoles) {
            fetchRoles();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadRoles]);

    const fetchRoles = () => {
        if (!canReadRoles) {
            toast.error('You do not have permission to view roles');
            return;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: {}
        };

        ApiHit(json, searchRole).then((res) => {
            if (res?.statusCode === 200) {
                const roleData = Array.isArray(res?.data?.docs) ? res?.data?.docs : [];
                dispatch(setApiJson(roleData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };
                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch roles. Please try again.');
            }
        }).catch(error => {
            console.error("Error fetching roles:", error);
            dispatch(setApiJson([]));
            toast.error('Error fetching roles. Please try again.');
        });
    };

    const handleEdit = (row) => {
        if (!canEditRoles) {
            toast.error('You do not have permission to edit roles');
            return;
        }
        navigate(`/role/edit/${row._id}`);
    };
    const handleDelete = (row) => {
        if (!canDeleteRoles) {
            toast.error('You do not have permission to delete roles');
            return;
        }

        const searchPayload = {
            page: 1,
            limit: 1,
            search: {
                roleId: row?._id
            }
        };

        ApiHit(searchPayload, searchUser).then((searchRes) => {
            if (searchRes?.data && searchRes.data.length > 0) {
                toast.error('This role is assigned to users, you can\'t delete it');
                return;
            }

            const deletePayload = {
                _id: row?._id
            };

            ApiHit(deletePayload, deleteRole).then((deleteRes) => {
                if (deleteRes?.statusCode === 200) {
                    toast.success('Role deleted successfully!');
                    fetchRoles();
                } else {
                    toast.error(deleteRes?.message || 'Failed to delete role. Please try again.');
                }
            }).catch(error => {
                console.error("Error deleting role:", error);
                toast.error('Error occurred while deleting role. Please try again.');
            });

        }).catch(error => {
            console.error("Error checking role assignment:", error);
            toast.error('Error occurred while checking role assignment. Please try again.');
        });
    };

    const renderStatus = (status) => {
        if (status === undefined || status === null) return null;

        const isActive = status === "active";
        const bgColor = isActive ? "bg-green-100" : "bg-red-100";
        const textColor = isActive ? "text-green-800" : "text-red-800";
        const label = isActive ? "Active" : "Inactive";

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {label}
            </span>
        );
    };

    const renderPermissions = (permissions) => {
        if (!permissions || !Array.isArray(permissions)) return 'N/A';

        const activePermissions = permissions.filter(perm => {
            const hasReadAccess = perm.permission?.[0]?.read;
            const hasWriteAccess = perm.permission?.[0]?.write;
            const hasDeleteAccess = perm.permission?.[0]?.delete;
            return hasReadAccess || hasWriteAccess || hasDeleteAccess;
        });

        return (
            <div className="flex md:justify-start justify-end flex-wrap gap-1">
                {activePermissions.map((perm, index) => (
                    <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {perm.value}
                    </span>
                ))}
                {activePermissions.length === 0 && (
                    <span className="text-gray-500 text-xs">No permissions</span>
                )}
            </div>
        );
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Role Name",
            key: "roleName",
            render: (value) => (
                <div className="flex items-center justify-end md:justify-start">
                    <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center mr-2">
                        <span className="text-sm font-medium text-blue-600">
                            {value && value.charAt(0).toUpperCase()}
                        </span>
                    </div>
                    <span className="font-medium">{value || 'N/A'}</span>
                </div>
            )
        },
        {
            title: "Permissions",
            key: "permission",
            render: renderPermissions
        },
        {
            title: "Status",
            key: "status",
            render: renderStatus
        },
        {
            title: "Actions",
            key: "actions",
            align: "center",
            render: (value, row) => {
                // Check if user has any action permissions
                const hasAnyPermission = canEditRoles || canDeleteRoles;

                if (!hasAnyPermission) {
                    return (
                        <div className="flex justify-center">
                            <span className="text-sm text-gray-400">No actions available</span>
                        </div>
                    );
                }

                return (
                    <div className="flex justify-end md:justify-center space-x-2">
                        {canEditRoles && (
                            <ActionButton
                                onClick={() => handleEdit(row)}
                                tooltip="Edit Role"
                                icon={<PencilIcon className='w-4 h-4' />}
                            />
                        )}

                        {canDeleteRoles && (
                            <DeleteActionButton
                                onConfirm={() => handleDelete(row)}
                                itemName="role"
                                tooltip="Delete Role"
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    // Filter out Actions column if user has no action permissions
    const hasAnyActionPermission = canEditRoles || canDeleteRoles;
    const filteredHeaders = hasAnyActionPermission
        ? tableHeaders
        : tableHeaders.filter(header => header.key !== 'actions');

    // Show access denied message if user doesn't have read permission
    if (!canReadRoles) {
        return (
            <div >
                <PageHeader
                    title={'Role Management'}
                    description={'View and manage user roles and permissions'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view roles.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div >
            <PageHeader
                title={'Role Management'}
                description={'View and manage user roles and permissions'}
            />

            <div>
                <AppTable
                    TH={filteredHeaders}
                    TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                    emptyMessage="No roles found"
                    buttonText={canAddRoles ? "Add Role" : undefined}
                    navigateTo={canAddRoles ? "/role/add" : undefined}
                />
            </div>
        </div>
    );
}

export default RoleMaster;
